// Cutscene player (rebuild.md §7.7): full-bleed 16:9 on black, starts on the player's tap, speaker-styled
// captions rendered by the player (native track hidden), auto-hiding controls with chapter ticks, CC,
// quality, hold-to-skip and "Skip the Guide", Screen Wake Lock, per-session resume, and the motion-comic
// fallback on any decode or network error.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { audio } from '@audio/engine.ts';
import { setExcerptPlaying } from '@audio/barks.ts';
import { settings, updateSettings } from '@app/state.ts';
import { chooseRendition, excerptMedia, loadMediaManifest, mediaManifest, mediaUrl } from './manifest.ts';
import { isDownloaded } from './packs.ts';
import { activeCues, parseVtt, speakerClass, type Cue } from './vtt.ts';
import { MotionComic } from './MotionComic.tsx';
import './media.css';

export interface CutsceneProps {
  id: string;
  role: 'pre' | 'post';
  firstViewing: boolean;
  onEnded: () => void;
  onSkip: () => void;
  /** Start playing without the tap-to-play card (Theatre "play all" continues automatically). */
  autoStart?: boolean | undefined;
  /** Play only the Guide's part (up to the chapter-card marker). */
  guideOnly?: boolean | undefined;
  title?: string | undefined;
}

const RESUME_KEY = (id: string) => `forge.resume.${id}`;
const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export function CutscenePlayer(p: CutsceneProps) {
  const [ready, setReady] = useState(!!excerptMedia(p.id)?.video['720']);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    void loadMediaManifest().then(() => setReady(true));
  }, []);
  const e = excerptMedia(p.id);
  if (failed || (ready && e && !Object.keys(e.video).length)) {
    return <MotionComic excerptId={p.id} notice={failed ?? 'The film isn’t available offline for this chapter, so here are the graphic-novel pages.'} onEnded={p.onEnded} onSkip={p.onSkip} />;
  }
  if (!ready || !e) return <main id="main" class="cutscene cutscene-loading" aria-busy="true" />;
  return <Player {...p} onError={(msg) => setFailed(msg)} key={`${p.id}-${mediaManifest.value.hash ?? ''}`} />;
}

function Player(p: CutsceneProps & { onError: (msg: string) => void }) {
  const e = excerptMedia(p.id)!;
  const st = settings.value;
  const video = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(!!p.autoStart);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [cues, setCues] = useState<Cue[]>([]);
  const [controls, setControls] = useState(true);
  const [skipVisible, setSkipVisible] = useState(!p.firstViewing);
  const [holding, setHolding] = useState(false);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rendition = chooseRendition(e, st.videoQuality, isDownloaded(e.stage));
  const src = mediaUrl(e.video[rendition]?.url ?? e.video['720']?.url ?? null);
  const guideEnd = p.guideOnly && e.markers[1] ? e.markers[1].at / 24 : null;
  const chapterMarker = e.markers.find((m, i) => i > 0 && !/guide/i.test(m.label));

  // Offline with the film not in a downloaded pack: go straight to the comic. Mobile browsers often skip
  // `preload="metadata"`, so the video element alone would only fail after the player taps play.
  useEffect(() => {
    if (!src || navigator.onLine || typeof caches === 'undefined') return;
    let alive = true;
    void caches.match(src, { ignoreSearch: true }).then((hit) => {
      if (alive && !hit) p.onError('You’re offline and this chapter isn’t downloaded, so here are the graphic-novel pages instead.');
    });
    return () => {
      alive = false;
    };
  }, [src]);

  // captions (rendered by the player, styled by speaker)
  useEffect(() => {
    const url = mediaUrl(e.vtt);
    if (!url) return;
    void fetch(url)
      .then((r) => (r.ok ? r.text() : ''))
      .then((txt) => setCues(parseVtt(txt)))
      .catch(() => setCues([]));
  }, [e.vtt]);

  // game audio fades out; barks never overlap an excerpt; wake lock while playing
  useEffect(() => {
    audio.excerptMode(true);
    setExcerptPlaying(true);
    let lock: { release: () => Promise<void> } | null = null;
    const nav = navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } };
    if (started) void nav.wakeLock?.request('screen').then((l) => (lock = l)).catch(() => undefined);
    return () => {
      audio.excerptMode(false);
      setExcerptPlaying(false);
      void lock?.release();
    };
  }, [started]);

  useEffect(() => {
    if (!p.firstViewing) return;
    const id = setTimeout(() => setSkipVisible(true), 2000);
    return () => clearTimeout(id);
  }, []);

  const poke = () => {
    setControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setControls(false), 2800);
  };

  const start = async () => {
    setStarted(true);
    audio.unlock();
    const v = video.current;
    if (!v) return;
    const resume = Number(sessionStorage.getItem(RESUME_KEY(p.id)) ?? 0);
    if (resume > 1 && resume < e.seconds - 2) v.currentTime = resume;
    try {
      await v.play();
    } catch {
      /* autoplay refused: the big play button stays */
    }
    poke();
  };

  useEffect(() => {
    if (p.autoStart) void start();
  }, []);

  const finish = () => {
    sessionStorage.removeItem(RESUME_KEY(p.id));
    p.onEnded();
  };

  const onTime = () => {
    const v = video.current;
    if (!v) return;
    setT(v.currentTime);
    if (Math.floor(v.currentTime) % 3 === 0) sessionStorage.setItem(RESUME_KEY(p.id), String(v.currentTime));
    if (guideEnd !== null && v.currentTime >= guideEnd) {
      v.pause();
      finish();
    }
  };

  const toggle = () => {
    const v = video.current;
    if (!v) return;
    if (v.paused) void v.play();
    else v.pause();
    poke();
  };

  const startHold = () => {
    setHolding(true);
    holdTimer.current = setTimeout(() => {
      setHolding(false);
      sessionStorage.removeItem(RESUME_KEY(p.id));
      p.onSkip();
    }, 600);
  };
  const endHold = () => {
    setHolding(false);
    if (holdTimer.current) clearTimeout(holdTimer.current);
  };

  const onKey = (ev: KeyboardEvent) => {
    const v = video.current;
    if (!v) return;
    if (ev.key === ' ' || ev.key === 'k') {
      ev.preventDefault();
      toggle();
    } else if (ev.key === 'ArrowLeft') v.currentTime = Math.max(0, v.currentTime - 10);
    else if (ev.key === 'ArrowRight') v.currentTime = Math.min(e.seconds, v.currentTime + 10);
    else if (ev.key === 'c') updateSettings({ captions: !st.captions });
    poke();
  };

  const visible = st.captions ? activeCues(cues, t) : [];
  const poster = mediaUrl(e.poster);
  const ticks = useMemo(() => e.markers.slice(1).map((m) => ({ pct: (m.at / 24 / e.seconds) * 100, label: m.label })), [e.id]);

  return (
    <main
      id="main"
      class={`cutscene${controls || !playing ? ' show-controls' : ''} cap-size-${st.captionSize}${st.captionBackground ? ' cap-bg' : ''}`}
      onPointerMove={poke}
      onKeyDown={onKey as never}
      aria-label={p.title ?? 'Film excerpt'}
    >
      <div class="cutscene-frame">
        <video
          ref={video}
          src={src ?? undefined}
          poster={poster ?? undefined}
          playsInline
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={onTime}
          onEnded={finish}
          onError={() => p.onError('The film couldn’t play here, so here are the graphic-novel pages instead.')}
          onClick={started ? toggle : undefined}
          aria-label={p.title ?? 'Film excerpt'}
        >
          {/* Native track for assistive tech and native fullscreen; not `default`, so the styled overlay below is what shows. */}
          <track kind="captions" src={mediaUrl(e.vtt) ?? undefined} srcLang="en" label="English" />
        </video>
        {visible.length > 0 && (
          <div class="captions" aria-live="off">
            {visible.map((c) => (
              <p key={c.id} class={`cap ${speakerClass(c.speaker)}`}>
                {c.speaker && c.speaker !== 'Guide' && <span class="cap-speaker">{c.speaker}: </span>}
                {c.text}
              </p>
            ))}
          </div>
        )}
        {!started && (
          <button type="button" class="cutscene-start" onClick={() => void start()} ref={(el) => el?.focus()}>
            <span class="cutscene-start-icon" aria-hidden="true">
              ▶
            </span>
            <span>{p.title ?? 'Play'}</span>
            <span class="cutscene-start-len">{fmt(e.seconds)}</span>
          </button>
        )}
      </div>
      {started && (
        <div class="cutscene-controls" role="group" aria-label="Playback controls">
          <button type="button" class="cs-btn" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>
            {playing ? '❚❚' : '▶'}
          </button>
          <button
            type="button"
            class="cs-btn"
            onClick={() => {
              const v = video.current;
              if (v) v.currentTime = Math.max(0, v.currentTime - 10);
            }}
            aria-label="Back 10 seconds"
          >
            ↺10
          </button>
          <div class="cs-scrub">
            <input
              type="range"
              min={0}
              max={e.seconds}
              step={0.1}
              value={t}
              aria-label="Seek"
              aria-valuetext={`${fmt(t)} of ${fmt(e.seconds)}`}
              onInput={(ev) => {
                const v = video.current;
                if (v) v.currentTime = Number((ev.target as HTMLInputElement).value);
              }}
            />
            {ticks.map((k) => (
              <span key={k.label} class="cs-tick" style={{ left: `${k.pct}%` }} title={k.label} aria-hidden="true" />
            ))}
          </div>
          <span class="cs-time">
            {fmt(t)} / {fmt(e.seconds)}
          </span>
          <button type="button" class="cs-btn" aria-pressed={st.captions} onClick={() => updateSettings({ captions: !st.captions })} aria-label="Captions">
            CC
          </button>
          <select class="cs-quality" aria-label="Video quality" value={st.videoQuality} onChange={(ev) => updateSettings({ videoQuality: (ev.target as HTMLSelectElement).value as never })}>
            <option value="auto">Auto</option>
            <option value="1080">1080p</option>
            <option value="720">720p</option>
            <option value="480">Data saver</option>
          </select>
          {chapterMarker && t < chapterMarker.at / 24 - 1 && (
            <button
              type="button"
              class="cs-btn cs-text"
              onClick={() => {
                const v = video.current;
                if (v) v.currentTime = chapterMarker.at / 24;
              }}
            >
              Skip the Guide
            </button>
          )}
          {skipVisible && (
            <button
              type="button"
              class={`cs-btn cs-text cs-skip${holding ? ' holding' : ''}`}
              onPointerDown={startHold}
              onPointerUp={endHold}
              onPointerLeave={endHold}
              onKeyDown={(ev) => (ev.key === 'Enter' || ev.key === ' ') && (ev.preventDefault(), p.onSkip())}
              aria-label="Skip (hold)"
            >
              Skip <span class="cs-hold" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </main>
  );
}
